// assets/AllCourses/Exercises/CompleteTheSentencesExercise.jsx

import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function CompleteTheSentencesExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(
    exercise.sentenceCompletionQuestions
  )
    ? exercise.sentenceCompletionQuestions
    : [];

  /* ================= ENGINE ================= */
  const {
  answers,
  setAnswer,
  submitIfReady,
  getOptionState,
} = useExerciseEngine({
  questions,
  showResult: showResults,
  onComplete,
  externalAnswers: userAnswer || {}, 
});

 

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode)
      onUpdate("sentenceCompletionQuestions", next);
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[idx] || {
      text: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    if (field === "options") {
      const options = value
        .split(",")
        .map((o) => o.trim())
        .filter(Boolean);

      next[idx] = {
        ...current,
        options,
        answer: options.includes(current.answer)
          ? current.answer
          : "",
      };
    } else {
      next[idx] = { ...current, [field]: value };
    }

    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
 const handleChange = (qIdx, value) => {
  if (isEditMode || showResults) return;

  setAnswer(qIdx, value);

  submitIfReady();
};

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <TextareaField
                label={`Sentence ${idx + 1}`}
                value={q.text || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "text", v)
                }
                rows={3}
                required
              />

              <StringField
                label="Options (comma separated)"
                value={(q.options || []).join(", ")}
                onChange={(v) =>
                  updateQuestionField(idx, "options", v)
                }
              />

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "answer", v)
                }
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestionField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                text: "",
                options: [],
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No sentences available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Complete the Sentences</CardTitle>
        <CardDescription>
          Choose the correct option
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const selected = answers[qIdx];

          return (
            <div key={qIdx} className="space-y-4">
              {/* Sentence */}
              <div className="rounded-xl border bg-muted/40 p-5 font-medium">
                {q.text}
              </div>

              {/* Options */}
              <RadioGroup
                value={selected ?? ""}
                onValueChange={(v) =>
                  handleChange(qIdx, v)
                }
                disabled={showResults}
                className="space-y-3"
              >
                {q.options.map((option, oIdx) => {
                  const state = getOptionState(
                    selected,
                    option,
                    q.answer
                  );

                  return (
                    <div
                      key={oIdx}
                      onClick={() => handleChange(qIdx, option)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-4 transition cursor-pointer hover:bg-muted/60 active:scale-[0.98]",
                        state.className
                      )}
                    >
                      <RadioGroupItem
                        value={option}
                        id={`cts-${qIdx}-${oIdx}`}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <Label
                        htmlFor={`cts-${qIdx}-${oIdx}`}
                        className="flex-1 cursor-pointer"
                      >
                        {option}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}